import React, { FormEvent } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import FormHelperText from '@mui/material/FormHelperText'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface Agent {
  id: number
  username: string
}

interface CronJob {
  id: number
  name: string
  summary_template: string
  instructions_template: string | null
  cron_expression: string
  timezone: string
  enabled: boolean
  board_id: number
  board_name: string
  assignee_id: number | null
  requestor_id: number
  reviewer_id: number
}

interface Props {
  cron_job: CronJob
  agents: Agent[]
}

const COMMON_TIMEZONES = [
  'UTC',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'US/Eastern',
  'US/Central',
  'US/Mountain',
  'US/Pacific',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Australia/Sydney',
]

export default function Edit({ cron_job, agents }: Props) {
  const { data, setData, put, processing, errors } = useForm({
    cron_job: {
      name: cron_job.name,
      summary_template: cron_job.summary_template,
      instructions_template: cron_job.instructions_template || '',
      cron_expression: cron_job.cron_expression,
      timezone: cron_job.timezone,
      enabled: cron_job.enabled,
      assignee_id: cron_job.assignee_id ?? ('' as number | ''),
      requestor_id: cron_job.requestor_id,
      reviewer_id: cron_job.reviewer_id,
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    put(`/manage/cron_jobs/${cron_job.id}`)
  }

  function handleDelete() {
    if (confirm(`Are you sure you want to delete cron job '${cron_job.name}'? This cannot be undone.`)) {
      router.delete(`/manage/cron_jobs/${cron_job.id}`)
    }
  }

  return (
    <SidebarLayout section="manage" title="Edit cron job">
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 600,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Board
          </Typography>
          <Typography
            variant="body2"
            sx={{
              backgroundColor: 'action.hover',
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
            }}
          >
            {cron_job.board_name}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={data.cron_job.name}
            onChange={e => setData('cron_job', { ...data.cron_job, name: e.target.value })}
            error={'cron_job.name' in errors}
            helperText={(errors as Record<string, string>)['cron_job.name']}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Summary Template"
            fullWidth
            multiline
            rows={2}
            value={data.cron_job.summary_template}
            onChange={e => setData('cron_job', { ...data.cron_job, summary_template: e.target.value })}
            error={'cron_job.summary_template' in errors}
            helperText={(errors as Record<string, string>)['cron_job.summary_template'] || 'Liquid syntax: {{ date }}, {{ week_number }}, {{ day_name }}, etc.'}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Instructions Template (optional)"
            fullWidth
            multiline
            rows={4}
            value={data.cron_job.instructions_template}
            onChange={e => setData('cron_job', { ...data.cron_job, instructions_template: e.target.value })}
            error={'cron_job.instructions_template' in errors}
            helperText={(errors as Record<string, string>)['cron_job.instructions_template']}
            sx={{ mb: 2.5 }}
          />
          <TextField
            label="Cron Expression"
            fullWidth
            value={data.cron_job.cron_expression}
            onChange={e => setData('cron_job', { ...data.cron_job, cron_expression: e.target.value })}
            error={'cron_job.cron_expression' in errors}
            helperText={(errors as Record<string, string>)['cron_job.cron_expression'] || 'e.g. "0 9 * * 1" = every Monday at 9am'}
            sx={{ mb: 2.5 }}
          />
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'cron_job.timezone' in errors}>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={data.cron_job.timezone}
              label="Timezone"
              onChange={e => setData('cron_job', { ...data.cron_job, timezone: e.target.value })}
            >
              {COMMON_TIMEZONES.map(tz => (
                <MenuItem key={tz} value={tz}>{tz}</MenuItem>
              ))}
            </Select>
            {'cron_job.timezone' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['cron_job.timezone']}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'cron_job.requestor_id' in errors}>
            <InputLabel>Requestor</InputLabel>
            <Select
              value={data.cron_job.requestor_id}
              label="Requestor"
              onChange={e => setData('cron_job', { ...data.cron_job, requestor_id: e.target.value as number })}
            >
              {agents.map(agent => (
                <MenuItem key={agent.id} value={agent.id}>{agent.username}</MenuItem>
              ))}
            </Select>
            {'cron_job.requestor_id' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['cron_job.requestor_id']}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'cron_job.reviewer_id' in errors}>
            <InputLabel>Reviewer</InputLabel>
            <Select
              value={data.cron_job.reviewer_id}
              label="Reviewer"
              onChange={e => setData('cron_job', { ...data.cron_job, reviewer_id: e.target.value as number })}
            >
              {agents.map(agent => (
                <MenuItem key={agent.id} value={agent.id}>{agent.username}</MenuItem>
              ))}
            </Select>
            {'cron_job.reviewer_id' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['cron_job.reviewer_id']}</FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <InputLabel>Assignee (optional)</InputLabel>
            <Select
              value={data.cron_job.assignee_id}
              label="Assignee (optional)"
              onChange={e => setData('cron_job', { ...data.cron_job, assignee_id: e.target.value as number | '' })}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {agents.map(agent => (
                <MenuItem key={agent.id} value={agent.id}>{agent.username}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={data.cron_job.enabled}
                onChange={e => setData('cron_job', { ...data.cron_job, enabled: e.target.checked })}
              />
            }
            label="Enabled"
            sx={{ mb: 2.5 }}
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Update cron job
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/manage/cron_jobs" style={{ color: '#1a1a1a' }}>Back to cron jobs</Link>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography variant="h3" color="error" sx={{ mb: 1 }}>
            Danger zone
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Deleting this cron job will permanently remove it. Existing tasks created by this cron job will not be affected.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
          >
            Delete cron job
          </Button>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
